import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight, BookOpen, Calendar } from "lucide-react";
import { Seo } from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white min-h-screen text-slate-800" data-testid="blog-list-page">
      <Seo
        title="Financial Tips & Insights | Ashish Ranade Wealth Educator Blog"
        description="Free, easy-to-understand articles on budgeting, mutual funds, SIPs, insurance, tax saving and wealth creation from Ashish Ranade, Ahmedabad."
        canonical="https://ashishranade.com/blog"
      />

      {/* Hero */}
      <section className="bg-[#0F172A] text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-left">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-[#D4AF37] mb-6">
            <BookOpen className="h-3.5 w-3.5" /> Wealth Educator Blog
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Money made <span className="text-[#D4AF37]">simple.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 font-light max-w-2xl">
            Straightforward, jargon-free articles to help you budget better,
            invest smarter and build lasting wealth — written for everyday
            Indians.
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        {loading ? (
          <p className="text-slate-500" data-testid="blog-loading">Loading articles…</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-20" data-testid="blog-empty">
            <p className="text-slate-500">No articles published yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group flex flex-col rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <div className="h-40 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] relative overflow-hidden">
                  {post.cover_image ? (
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BookOpen className="h-10 w-10 text-[#D4AF37]/50" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-[#D4AF37] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>
                <div className="flex flex-col flex-grow p-6">
                  <h2 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-[#1E3A8A] transition-colors">
                    {post.title}
                  </h2>
                  <p className="mt-3 text-sm text-slate-500 font-light line-clamp-3 flex-grow">
                    {post.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between text-xs text-slate-400">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" /> {formatDate(post.created_at)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {post.read_minutes} min read
                    </span>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A]">
                    Read article
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default BlogList;
